import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { createFileRoute, Link } from "@tanstack/react-router";
import {
  AlertTriangle,
  ArrowRightLeft,
  ChevronsLeftRight,
  Flame,
  KanbanSquare,
  MoreHorizontal,
  Search,
  User,
  X,
} from "lucide-react";
import { useMemo, useState } from "react";
import { toast } from "sonner";
import { FunnelTriage } from "@/components/crm/FunnelTriage";
import { SaleDialog } from "@/components/crm/SaleDialog";
import { Avatar } from "@/components/ui/avatar";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { EmptyState } from "@/components/ui/empty-state";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select } from "@/components/ui/select";
import { Skeleton } from "@/components/ui/skeleton";
import { Hint } from "@/components/ui/tooltip";
import { api, ApiError } from "@/lib/api";
import { formatCurrency, formatPhone, relativeTime, sourceGroup, sourceLabel } from "@/lib/crm";
import { cn } from "@/lib/utils";

export const Route = createFileRoute("/_authenticated/pipeline")({
  component: PipelinePage,
});

type Deal = {
  id: string;
  title: string;
  value: string | null;
  status: "open" | "won" | "lost";
  stageId: string;
  stageChangedAt: string;
  lossReason: string | null;
  leadId: string | null;
  stalled: boolean;
  owner: { id: string; fullName: string | null } | null;
  contact: { id: string; fullName: string | null; phoneNormalized: string | null };
  leadSource: string | null;
};

type Column = {
  id: string;
  name: string;
  color: string | null;
  isWon: boolean;
  isLost: boolean;
  stalledAfterHours: number | null;
  total: number;
  sum: number;
  deals: Deal[];
};

type Board = { pipelineId: string | null; stages: Column[] };
type Pipeline = { id: string; name: string; isDefault: boolean };

/** Ponto de cor da coluna, pela cor da etiqueta configurada na etapa. */
const DOT: Record<string, string> = {
  slate: "bg-slate-400",
  rose: "bg-rose-500",
  amber: "bg-amber-500",
  emerald: "bg-emerald-500",
  sky: "bg-sky-500",
  violet: "bg-violet-500",
};

const CHAVE_RECOLHIDAS = "rn.pipeline.recolhidas";

function lerRecolhidas(): Set<string> {
  try {
    const bruto = localStorage.getItem(CHAVE_RECOLHIDAS);
    return new Set(bruto ? (JSON.parse(bruto) as string[]) : []);
  } catch {
    return new Set();
  }
}

/**
 * Kanban do funil.
 *
 * Ocupa a altura inteira da tela: o cabeçalho é uma linha, o quadro é o
 * resto, e o que rola é cada coluna por dentro — nunca a página. Era a
 * reclamação número um: com 280 cards, o quadro descia por três telas e a
 * coluna "Agendado" só existia para quem rolasse até lá.
 *
 * Arrastar e soltar com a API nativa do HTML, sem biblioteca: o gesto aqui é
 * um só — pegar um card e largar numa coluna. No celular, onde o arrastar
 * nativo não existe, o menu do card faz o mesmo movimento por toque.
 *
 * Largar numa coluna de perda abre o diálogo de motivo antes de gravar, pelo
 * mesmo motivo da ficha do lead: o servidor recusaria sem motivo, e é melhor
 * perguntar do que devolver erro depois do gesto.
 */
function PipelinePage() {
  const queryClient = useQueryClient();
  const [pipelineId, setPipelineId] = useState("");
  const [dragging, setDragging] = useState<string | null>(null);
  const [over, setOver] = useState<string | null>(null);
  const [busca, setBusca] = useState("");
  const [soParados, setSoParados] = useState(false);
  const [perguntando, setPerguntando] = useState<{ dealId: string; stageId: string } | null>(null);
  // Depois de ganhar, oferecemos registrar a venda na hora: é o momento em que
  // a informação existe e a pessoa está com ela na cabeça. Pedir depois, numa
  // tela separada, é como o dado de venda deixa de ser preenchido.
  const [vendendo, setVendendo] = useState<{
    contactId: string;
    dealId: string;
    contactName: string | null;
  } | null>(null);

  /**
   * Colunas recolhidas a uma faixa estreita, lembradas entre sessões.
   *
   * É o que faz o quadro caber numa tela: ganho e perdido são histórico e
   * começam recolhidos; qualquer outra coluna pode ser recolhida por quem
   * não precisa dela naquele dia.
   */
  const [recolhidas, setRecolhidas] = useState<Set<string>>(lerRecolhidas);
  const [inicializou, setInicializou] = useState(false);
  const alternarRecolhida = (id: string) => {
    const novo = new Set(recolhidas);
    if (novo.has(id)) novo.delete(id);
    else novo.add(id);
    setRecolhidas(novo);
    try {
      localStorage.setItem(CHAVE_RECOLHIDAS, JSON.stringify([...novo]));
    } catch {
      // Sem storage, só não lembra.
    }
  };

  const pipelines = useQuery({
    queryKey: ["pipelines"],
    queryFn: () => api.get<Pipeline[]>("/pipelines"),
  });

  const boardKey = ["deals", "board", pipelineId];
  const board = useQuery({
    queryKey: boardKey,
    queryFn: () => api.get<Board>("/deals", { pipelineId: pipelineId || undefined }),
    // O quadro automático move cards em segundo plano; quem está olhando
    // vê o card andar sem recarregar.
    refetchInterval: 30_000,
  });

  // Na primeira carga, sem preferência salva, ganho e perdido nascem
  // recolhidos. Ajuste durante a renderização (e não num efeito) para a
  // primeira pintura já sair certa.
  if (board.data && !inicializou) {
    setInicializou(true);
    if (!localStorage.getItem(CHAVE_RECOLHIDAS)) {
      const fechadas = board.data.stages.filter((s) => s.isWon || s.isLost).map((s) => s.id);
      if (fechadas.length > 0) setRecolhidas(new Set(fechadas));
    }
  }

  const move = useMutation({
    mutationFn: ({
      dealId,
      stageId,
      lossReason,
    }: {
      dealId: string;
      stageId: string;
      lossReason?: string;
    }) => api.patch(`/deals/${dealId}/stage`, { stageId, lossReason }),
    // Otimista: o card aparece na coluna nova no instante do gesto. Esperar a
    // resposta deixa o card "voltando" por meio segundo, e é isso que faz o
    // arrastar parecer que não pegou.
    onMutate: async ({ dealId, stageId }) => {
      await queryClient.cancelQueries({ queryKey: boardKey });
      const anterior = queryClient.getQueryData<Board>(boardKey);
      if (anterior) {
        let movido: Deal | undefined;
        const semEle = anterior.stages.map((s) => {
          const card = s.deals.find((d) => d.id === dealId);
          if (card) movido = card;
          return { ...s, deals: s.deals.filter((d) => d.id !== dealId) };
        });
        if (movido) {
          const atualizado = {
            ...movido,
            stageId,
            stageChangedAt: new Date().toISOString(),
            stalled: false,
          };
          queryClient.setQueryData<Board>(boardKey, {
            ...anterior,
            stages: semEle.map((s) =>
              s.id === stageId ? { ...s, deals: [atualizado, ...s.deals] } : s,
            ),
          });
        }
      }
      return { anterior };
    },
    onError: (e, _vars, ctx) => {
      if (ctx?.anterior) queryClient.setQueryData(boardKey, ctx.anterior);
      toast.error(e instanceof ApiError ? e.message : "Não foi possível mover");
    },
    onSuccess: () => setPerguntando(null),
    onSettled: async () => {
      await queryClient.invalidateQueries({ queryKey: ["deals"] });
    },
  });

  const moverPara = (dealId: string, coluna: Column) => {
    const origem = board.data?.stages.find((s) => s.deals.some((d) => d.id === dealId));
    if (origem?.id === coluna.id) return;

    if (coluna.isLost) {
      setPerguntando({ dealId, stageId: coluna.id });
      return;
    }

    const card = origem?.deals.find((d) => d.id === dealId);
    move.mutate(
      { dealId, stageId: coluna.id },
      {
        onSuccess: () => {
          if (coluna.isWon && card) {
            setVendendo({
              contactId: card.contact.id,
              dealId,
              contactName: card.contact.fullName,
            });
          }
        },
      },
    );
  };

  const soltar = (coluna: Column) => {
    const dealId = dragging;
    setDragging(null);
    setOver(null);
    if (dealId) moverPara(dealId, coluna);
  };

  // Filtro local: o quadro inteiro já está no navegador, e filtrar aqui
  // responde na hora — sem ida ao servidor a cada tecla.
  const termo = busca.trim().toLowerCase();
  const digitos = termo.replace(/\D/g, "");
  const colunas = useMemo(() => {
    const stages = board.data?.stages ?? [];
    if (!termo && !soParados) return stages;
    return stages.map((s) => ({
      ...s,
      deals: s.deals.filter((d) => {
        if (soParados && !d.stalled) return false;
        if (!termo) return true;
        const nome = (d.contact.fullName ?? d.title).toLowerCase();
        const fone = d.contact.phoneNormalized ?? "";
        return nome.includes(termo) || (digitos.length >= 3 && fone.includes(digitos));
      }),
    }));
  }, [board.data, termo, digitos, soParados]);

  const resumo = useMemo(() => {
    const abertas = (board.data?.stages ?? []).filter((s) => !s.isWon && !s.isLost);
    return {
      cards: abertas.reduce((n, s) => n + s.total, 0),
      valor: abertas.reduce((n, s) => n + s.sum, 0),
      parados: abertas.reduce((n, s) => n + s.deals.filter((d) => d.stalled).length, 0),
    };
  }, [board.data]);

  const filtrando = termo.length > 0 || soParados;

  return (
    <div className="flex h-full min-h-0 flex-col">
      {/* Cabeçalho de uma linha: título, números do funil e ferramentas. */}
      <div className="flex shrink-0 flex-wrap items-center gap-x-4 gap-y-2 border-b px-4 py-3 sm:px-6">
        <div className="flex min-w-0 items-center gap-3">
          <h1 className="font-display text-xl font-semibold tracking-tight">Pipeline</h1>
          {(pipelines.data?.length ?? 0) > 1 && (
            <Select
              value={pipelineId}
              onChange={(e) => setPipelineId(e.target.value)}
              aria-label="Funil"
              className="w-44"
            >
              {pipelines.data?.map((p) => (
                <option key={p.id} value={p.id}>
                  {p.name}
                </option>
              ))}
            </Select>
          )}
        </div>

        {board.data && (
          <div className="hidden items-center gap-1.5 text-xs text-muted-foreground md:flex">
            <span className="kpi text-sm text-foreground">{resumo.cards}</span> em aberto
            {resumo.valor > 0 && (
              <>
                <span className="mx-1 text-border">·</span>
                <span className="kpi text-sm text-foreground">{formatCurrency(resumo.valor)}</span>
              </>
            )}
            {resumo.parados > 0 && (
              <>
                <span className="mx-1 text-border">·</span>
                <button
                  type="button"
                  onClick={() => setSoParados((v) => !v)}
                  className={cn(
                    "inline-flex cursor-pointer items-center gap-1 rounded-full px-2 py-0.5 font-medium transition-colors",
                    soParados
                      ? "bg-destructive text-destructive-foreground"
                      : "bg-destructive/10 text-destructive hover:bg-destructive/15",
                  )}
                >
                  <AlertTriangle className="size-3" />
                  {resumo.parados} parado{resumo.parados === 1 ? "" : "s"}
                </button>
              </>
            )}
          </div>
        )}

        <div className="ml-auto flex flex-wrap items-center gap-2">
          <div className="relative">
            <Search className="pointer-events-none absolute left-2.5 top-1/2 size-3.5 -translate-y-1/2 text-muted-foreground" />
            <Input
              value={busca}
              onChange={(e) => setBusca(e.target.value)}
              placeholder="Filtrar por nome ou telefone"
              aria-label="Filtrar cards"
              className="h-8 w-44 pl-8 pr-7 text-xs sm:w-56"
            />
            {busca && (
              <button
                type="button"
                aria-label="Limpar filtro"
                onClick={() => setBusca("")}
                className="absolute right-1.5 top-1/2 inline-flex size-5 -translate-y-1/2 cursor-pointer items-center justify-center rounded text-muted-foreground hover:bg-muted hover:text-foreground"
              >
                <X className="size-3" />
              </button>
            )}
          </div>
          <FunnelTriage />
        </div>
      </div>

      {board.isLoading ? (
        <div className="flex flex-1 gap-3 overflow-hidden px-4 py-4 sm:px-6">
          {Array.from({ length: 5 }, (_, i) => (
            <Skeleton key={i} className="h-full w-64 shrink-0 rounded-2xl" />
          ))}
        </div>
      ) : board.data?.stages.length === 0 ? (
        <EmptyState
          icon={KanbanSquare}
          title="Nenhuma etapa configurada"
          description="Crie o funil em Configurações › Funis para o quadro aparecer aqui."
          action={
            <Button asChild variant="outline" size="sm">
              <Link to="/configuracoes">Abrir configurações</Link>
            </Button>
          }
          className="flex-1"
        />
      ) : (
        /* O quadro: rola na horizontal quando não cabe; cada coluna rola na
           vertical por dentro. No celular as colunas viram páginas com
           encaixe, uma por vez, e o dedo troca de etapa. */
        <div className="min-h-0 flex-1 overflow-x-auto overflow-y-hidden px-4 pb-4 pt-3 sm:px-6">
          <div className="flex h-full snap-x snap-mandatory gap-3 md:snap-none">
            {colunas.map((coluna) => {
              const recolhida = recolhidas.has(coluna.id);
              const alvo = over === coluna.id && dragging !== null;
              const aoArrastar = {
                onDragOver: (e: React.DragEvent) => {
                  e.preventDefault();
                  if (over !== coluna.id) setOver(coluna.id);
                },
                onDragLeave: () => setOver((v) => (v === coluna.id ? null : v)),
                onDrop: () => soltar(coluna),
              };

              if (recolhida) {
                return (
                  <button
                    key={coluna.id}
                    type="button"
                    {...aoArrastar}
                    onClick={() => alternarRecolhida(coluna.id)}
                    title={`Expandir ${coluna.name}`}
                    className={cn(
                      "flex h-full w-11 shrink-0 cursor-pointer flex-col items-center gap-3 rounded-2xl border bg-muted/40 py-3 transition-[background-color,box-shadow] hover:bg-muted",
                      alvo && "bg-primary/10 ring-2 ring-primary/50",
                    )}
                  >
                    <span
                      className={cn(
                        "size-2 rounded-full",
                        DOT[coluna.color ?? ""] ?? "bg-slate-400",
                      )}
                    />
                    <span className="kpi text-xs font-semibold">{coluna.total}</span>
                    <span
                      className="text-[11px] font-medium text-muted-foreground"
                      style={{ writingMode: "vertical-rl" }}
                    >
                      {coluna.name}
                    </span>
                    <ChevronsLeftRight className="mt-auto size-3.5 text-muted-foreground" />
                  </button>
                );
              }

              return (
                <section
                  key={coluna.id}
                  {...aoArrastar}
                  aria-label={coluna.name}
                  className={cn(
                    "flex h-full w-[85vw] shrink-0 snap-start flex-col rounded-2xl border bg-muted/40 transition-[background-color,box-shadow] duration-150",
                    "md:w-auto md:min-w-[15.5rem] md:flex-1 md:basis-0 xl:max-w-[21rem]",
                    alvo && "bg-primary/8 ring-2 ring-primary/50",
                  )}
                >
                  <header className="flex shrink-0 items-center gap-2 px-3 pb-2 pt-3">
                    <span
                      className={cn(
                        "size-2 shrink-0 rounded-full",
                        DOT[coluna.color ?? ""] ?? "bg-slate-400",
                      )}
                    />
                    <h2 className="min-w-0 flex-1 truncate text-[13px] font-semibold">
                      {coluna.name}
                    </h2>
                    <span className="kpi rounded-full bg-card px-2 py-0.5 text-[11px] font-semibold text-muted-foreground shadow-card">
                      {filtrando ? `${coluna.deals.length}/${coluna.total}` : coluna.total}
                    </span>
                    <Hint label="Recolher coluna" side="bottom">
                      <button
                        type="button"
                        aria-label={`Recolher ${coluna.name}`}
                        onClick={() => alternarRecolhida(coluna.id)}
                        className="inline-flex size-6 cursor-pointer items-center justify-center rounded-md text-muted-foreground/60 transition-colors hover:bg-card hover:text-foreground"
                      >
                        <ChevronsLeftRight className="size-3.5" />
                      </button>
                    </Hint>
                  </header>
                  {coluna.sum > 0 && (
                    <p className="kpi -mt-1 px-3 pb-2 text-[11px] text-muted-foreground">
                      {formatCurrency(coluna.sum)}
                    </p>
                  )}

                  <div className="scrollbar-hover min-h-0 flex-1 space-y-2 overflow-y-auto px-2 pb-2">
                    {coluna.deals.length === 0 ? (
                      <div
                        className={cn(
                          "flex h-24 items-center justify-center rounded-xl border border-dashed text-xs text-muted-foreground transition-colors",
                          alvo ? "border-primary/60 bg-primary/5 text-primary" : "border-border/80",
                        )}
                      >
                        {dragging ? "Solte aqui" : filtrando ? "Nada com esse filtro" : "Vazia"}
                      </div>
                    ) : (
                      coluna.deals.map((deal) => (
                        <DealCard
                          key={deal.id}
                          deal={deal}
                          arrastando={dragging === deal.id}
                          colunas={board.data?.stages ?? []}
                          colunaAtual={coluna}
                          onDragStart={() => setDragging(deal.id)}
                          onDragEnd={() => {
                            setDragging(null);
                            setOver(null);
                          }}
                          onMover={(destino) => moverPara(deal.id, destino)}
                        />
                      ))
                    )}
                  </div>
                </section>
              );
            })}
          </div>
        </div>
      )}

      <SaleDialog
        open={vendendo !== null}
        contactId={vendendo?.contactId ?? null}
        dealId={vendendo?.dealId}
        contactName={vendendo?.contactName}
        onClose={() => setVendendo(null)}
      />

      <LossDialog
        open={perguntando !== null}
        pending={move.isPending}
        onCancel={() => setPerguntando(null)}
        onConfirm={(lossReason) => perguntando && move.mutate({ ...perguntando, lossReason })}
      />
    </div>
  );
}

/** Idade do card na coluna, com cor: neutro recente, âmbar esfriando, vermelho parado. */
function Idade({ deal }: { deal: Deal }) {
  // Relógio lido uma vez por montagem: chamar `Date.now()` direto na
  // renderização é impuro para o React, e o quadro é recarregado a cada 30 s
  // de qualquer forma.
  const [agora] = useState(() => Date.now());
  const horas = (agora - new Date(deal.stageChangedAt).getTime()) / 3_600_000;
  const tom = deal.stalled
    ? "text-destructive"
    : horas > 48
      ? "text-warning-foreground dark:text-warning"
      : "text-muted-foreground";
  return (
    <span className={cn("inline-flex items-center gap-1 text-[11px] tabular-nums", tom)}>
      {deal.stalled && <AlertTriangle className="size-3" />}
      {relativeTime(deal.stageChangedAt).replace("há ", "")}
    </span>
  );
}

function DealCard({
  deal,
  arrastando,
  colunas,
  colunaAtual,
  onDragStart,
  onDragEnd,
  onMover,
}: {
  deal: Deal;
  arrastando: boolean;
  colunas: Column[];
  colunaAtual: Column;
  onDragStart: () => void;
  onDragEnd: () => void;
  onMover: (destino: Column) => void;
}) {
  const nome = deal.contact.fullName ?? deal.title;
  const grupo = sourceGroup(deal.leadSource);
  const valor = Number(deal.value ?? 0);

  return (
    <article
      draggable
      onDragStart={onDragStart}
      onDragEnd={onDragEnd}
      className={cn(
        "group relative cursor-grab rounded-xl border bg-card p-3 shadow-card transition-[box-shadow,transform,opacity] duration-150",
        "hover:-translate-y-px hover:shadow-panel active:cursor-grabbing",
        arrastando && "rotate-1 opacity-40",
        deal.stalled && "border-destructive/30",
      )}
    >
      <div className="flex items-start gap-2.5">
        <Avatar name={nome} seed={deal.contact.phoneNormalized ?? deal.id} size="sm" />
        <div className="min-w-0 flex-1">
          <p className="truncate text-[13px] font-semibold leading-tight">
            {deal.leadId ? (
              <Link
                to="/leads/$leadId"
                params={{ leadId: deal.leadId }}
                className="hover:text-primary hover:underline"
              >
                {nome}
              </Link>
            ) : (
              nome
            )}
          </p>
          <p className="truncate text-[11px] text-muted-foreground">
            {formatPhone(deal.contact.phoneNormalized)}
          </p>
        </div>

        {/* O menu faz por toque o que o arrastar faz por mouse. */}
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <button
              type="button"
              aria-label="Ações do card"
              className="-mr-1 -mt-1 inline-flex size-7 shrink-0 cursor-pointer items-center justify-center rounded-md text-muted-foreground/60 opacity-0 transition-opacity hover:bg-muted hover:text-foreground focus-visible:opacity-100 group-hover:opacity-100 data-[state=open]:opacity-100 md:opacity-0 max-md:opacity-100"
            >
              <MoreHorizontal className="size-4" />
            </button>
          </DropdownMenuTrigger>
          <DropdownMenuContent align="end" className="w-52">
            {deal.leadId && (
              <>
                <DropdownMenuItem asChild>
                  <Link to="/leads/$leadId" params={{ leadId: deal.leadId }}>
                    <User />
                    Abrir ficha do lead
                  </Link>
                </DropdownMenuItem>
                <DropdownMenuSeparator />
              </>
            )}
            <DropdownMenuLabel>Mover para</DropdownMenuLabel>
            {colunas
              .filter((c) => c.id !== colunaAtual.id)
              .map((c) => (
                <DropdownMenuItem key={c.id} onSelect={() => onMover(c)}>
                  <ArrowRightLeft />
                  <span className="flex-1 truncate">{c.name}</span>
                  {(c.isWon || c.isLost) && (
                    <span className="text-[10px] text-muted-foreground">
                      {c.isWon ? "ganho" : "perda"}
                    </span>
                  )}
                </DropdownMenuItem>
              ))}
          </DropdownMenuContent>
        </DropdownMenu>
      </div>

      <div className="mt-2.5 flex flex-wrap items-center gap-1.5">
        {valor > 0 && (
          <span className="kpi text-xs font-semibold text-foreground">{formatCurrency(valor)}</span>
        )}
        {/* Cor por balde, não por origem: são três perguntas (pago, rede
            social, orgânico), e doze cores diferentes ninguém decora. */}
        {deal.leadSource && (
          <Badge
            variant={grupo === "pago" ? "warning" : grupo === "social" ? "info" : "muted"}
            className="max-w-[9rem] truncate"
          >
            {grupo === "pago" && <Flame />}
            {sourceLabel(deal.leadSource)}
          </Badge>
        )}
        {deal.lossReason && <Badge variant="danger">{deal.lossReason}</Badge>}
      </div>

      <div className="mt-2 flex items-center justify-between gap-2 border-t pt-2">
        <Idade deal={deal} />
        {deal.owner?.fullName ? (
          <Hint label={deal.owner.fullName} side="left">
            <span>
              <Avatar name={deal.owner.fullName} size="xs" />
            </span>
          </Hint>
        ) : (
          <span className="text-[10px] text-muted-foreground/70">sem responsável</span>
        )}
      </div>
    </article>
  );
}

function LossDialog({
  open,
  pending,
  onCancel,
  onConfirm,
}: {
  open: boolean;
  pending: boolean;
  onCancel: () => void;
  onConfirm: (reason: string) => void;
}) {
  const [reason, setReason] = useState("");
  const sugestoes = useQuery({
    queryKey: ["loss-reasons"],
    queryFn: () => api.get<string[]>("/pipelines/loss-reasons"),
    enabled: open,
  });

  return (
    <Dialog
      open={open}
      onOpenChange={(next) => {
        if (!next) {
          setReason("");
          onCancel();
        }
      }}
    >
      <DialogContent>
        <form
          onSubmit={(e) => {
            e.preventDefault();
            onConfirm(reason.trim());
          }}
        >
          <DialogHeader>
            <DialogTitle>Por que este negócio foi perdido?</DialogTitle>
            <DialogDescription>
              Os motivos configurados aparecem abaixo. Usar sempre os mesmos é o que faz o relatório
              agrupar em vez de virar texto solto.
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-3 py-4">
            {(sugestoes.data?.length ?? 0) > 0 && (
              <div className="flex flex-wrap gap-2">
                {sugestoes.data?.map((s) => (
                  <button
                    key={s}
                    type="button"
                    onClick={() => setReason(s)}
                    className={cn(
                      "cursor-pointer rounded-full border px-3 py-1 text-xs transition-colors",
                      reason === s ? "border-primary bg-primary/10 text-primary" : "hover:bg-muted",
                    )}
                  >
                    {s}
                  </button>
                ))}
              </div>
            )}
            <div className="space-y-1.5">
              <Label htmlFor="deal-loss-reason">Motivo</Label>
              <Input
                id="deal-loss-reason"
                required
                minLength={2}
                maxLength={200}
                value={reason}
                onChange={(e) => setReason(e.target.value)}
                placeholder="Escolha acima ou escreva"
              />
            </div>
          </div>

          <DialogFooter>
            <Button type="button" variant="ghost" onClick={onCancel}>
              Cancelar
            </Button>
            <Button
              type="submit"
              variant="destructive"
              disabled={pending || reason.trim().length < 2}
            >
              {pending ? "Salvando…" : "Marcar como perdido"}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
